import { type InfiniteData } from '@tanstack/react-query';
import { flatMap } from 'lodash';

import { type Board, type Clip, type ClipAndBoardListItem, type ListResponse } from '@air/api/types';

export type ListViewItem =
  | { type: 'board'; board: Board; id: string }
  | { type: 'asset' | 'file'; clip: Clip; id: string };

export type BoardListViewItem = Extract<ListViewItem, { type: 'board' }>;
export type ClipListViewItem = Extract<ListViewItem, { type: 'asset' | 'file' }>;

export const getClipListViewItem = (clip: Clip): ClipListViewItem => ({
  clip,
  id: clip.id,
  type: clip.type === 'nonMedia' ? 'file' : 'asset',
});

export const getBoardListViewItem = (board: Board): BoardListViewItem => ({
  board,
  id: board.id,
  type: 'board',
});

const isBoard = (data: object): data is Board => 'title' in data && 'workspaceId' in data;

const isClip = (data: object): data is Clip => 'assetId' in data && 'id' in data;

const parseListViewItem = (item: ClipAndBoardListItem): ListViewItem | undefined => {
  if (item.type === 'board' && isBoard(item.data)) {
    return { ...getBoardListViewItem(item.data), id: item.id };
  }

  if ((item.type === 'asset' || item.type === 'file') && isClip(item.data)) {
    return { ...getClipListViewItem(item.data), id: item.id, type: item.type };
  }

  return undefined;
};

export const formatItemsListResponse = (data: InfiniteData<ListResponse<ClipAndBoardListItem>> | undefined) => {
  const items = flatMap(data?.pages, (page) =>
    page.data.map(parseListViewItem).filter((item): item is ListViewItem => item != null),
  );
  const boards = items.flatMap((item) => (item.type === 'board' ? [item.board] : []));
  const clips = items.flatMap((item) => (item.type === 'board' ? [] : [item.clip]));

  return {
    boards,
    clips,
    items,
    total: data?.pages?.[0]?.total,
  };
};
